import {useState} from 'react';

/**
 * useLocalStorage hook that persists its state in localStorage.
 * The custom setter immediately writes the new value to localStorage.
 *
 * @param key A string key for localStorage.
 * @param initialValue The default value if nothing is found in localStorage.
 * @returns A tuple [value, setValue] just like useState.
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
   // Lazy initialization: try to get the value from localStorage
   const [storedValue, setStoredValueInternal] = useState<T>(() => {
      try {
         const item = window.localStorage.getItem(key);
         return item ? (JSON.parse(item) as T) : initialValue;
      } catch (error) {
         console.error(`useLocalStorage: Error reading ${key} from localStorage`, error);
         return initialValue;
      }
   });

   // Custom setter that saves the new value to localStorage immediately
   const setStoredValue = (value: T | ((prevValue: T) => T)) => {
      setStoredValueInternal((prevValue: T) => {
         // Resolve value if it's a function
         const valueToStore = value instanceof Function ? value(prevValue) : value;
         try {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
         } catch (error) {
            console.error(`useLocalStorage: Error writing ${key} to localStorage`, error);
         }
         return valueToStore;
      });
   };

   return [storedValue, setStoredValue] as const;
}
